import React, { useState } from "react";
import {
  makeStyles,
  Button,
  Menu,
  MenuItem,
  Chip,
  Box,
  Typography,
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  container: {
    display: "inline-flex",
    alignItems: "center",
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(0, 0, 0, 0.35)"
        : "rgba(0, 0, 0, 0.04)",
    border: `1px solid ${
      theme.palette.type === "dark"
        ? "rgba(255, 255, 255, 0.08)"
        : "rgba(0, 0, 0, 0.08)"
    }`,
    borderRadius: 10,
    padding: 3,
    gap: 4,
    maxWidth: "100%",
    overflowX: "auto",
    ...theme.scrollbarStyles,
  },
  fullWidth: {
    display: "flex",
    width: "100%",
  },
  tabButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 7,
    padding: "6px 14px",
    borderRadius: 8,
    fontSize: "0.82rem",
    fontWeight: 500,
    textTransform: "none",
    letterSpacing: "0.2px",
    minHeight: 34,
    border: "1px solid transparent",
    color:
      theme.palette.type === "dark"
        ? "rgba(255, 255, 255, 0.65)"
        : "rgba(0, 0, 0, 0.65)",
    backgroundColor: "transparent",
    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
    cursor: "pointer",
    whiteSpace: "nowrap",
    flexShrink: 0,
    "&:hover": {
      color: theme.palette.type === "dark" ? "#FFFFFF" : "#111827",
      backgroundColor:
        theme.palette.type === "dark"
          ? "rgba(255, 255, 255, 0.06)"
          : "rgba(0, 0, 0, 0.05)",
    },
  },
  tabButtonActive: {
    backgroundColor: `${theme.palette.primary.main}22 !important`,
    color: `${theme.palette.primary.main} !important`,
    border: `1px solid ${theme.palette.primary.main}66 !important`,
    fontWeight: "600 !important",
    boxShadow: `0 2px 8px ${theme.palette.primary.main}26`,
    "& $badge": {
      backgroundColor: `${theme.palette.primary.main}33`,
      color: theme.palette.primary.main,
      borderColor: `${theme.palette.primary.main}55`,
    },
  },
  tabButtonFull: {
    flex: 1,
    minWidth: 0,
    padding: "6px 8px",
    fontSize: "0.78rem",
    gap: 5,
  },
  tabButtonDisabled: {
    opacity: "0.38 !important",
    cursor: "not-allowed !important",
    pointerEvents: "none",
  },
  label: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  icon: {
    display: "inline-flex",
    alignItems: "center",
    fontSize: "1.1rem",
    marginRight: 2,
    "& svg": {
      fontSize: "1.15rem",
    },
  },
  badge: {
    fontSize: "0.72rem",
    fontWeight: 700,
    padding: "1px 6px",
    borderRadius: 999,
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(0, 0, 0, 0.3)"
        : "rgba(0, 0, 0, 0.06)",
    color:
      theme.palette.type === "dark"
        ? "rgba(255, 255, 255, 0.7)"
        : "rgba(0, 0, 0, 0.7)",
    border: `1px solid ${
      theme.palette.type === "dark"
        ? "rgba(255, 255, 255, 0.1)"
        : "rgba(0, 0, 0, 0.1)"
    }`,
    lineHeight: 1.3,
  },
  moreMenuPaper: {
    backgroundColor: theme.palette.type === "dark" ? "#1E2126" : "#FFFFFF",
    borderRadius: 12,
    border: `1px solid ${
      theme.palette.type === "dark"
        ? "rgba(255, 255, 255, 0.1)"
        : "rgba(0, 0, 0, 0.1)"
    }`,
    boxShadow: "0 12px 32px rgba(0, 0, 0, 0.35)",
    padding: 4,
    minWidth: 170,
  },
  moreMenuItem: {
    borderRadius: 8,
    margin: "2px 0",
    padding: "8px 12px",
    fontSize: "0.82rem",
    fontWeight: 500,
    display: "flex",
    alignItems: "center",
    gap: 8,
    color:
      theme.palette.type === "dark"
        ? "rgba(255, 255, 255, 0.85)"
        : "rgba(0, 0, 0, 0.85)",
    "&:hover": {
      backgroundColor:
        theme.palette.type === "dark"
          ? "rgba(255, 255, 255, 0.08)"
          : "rgba(0, 0, 0, 0.05)",
    },
  },
  moreMenuItemActive: {
    backgroundColor: `${theme.palette.primary.main}22 !important`,
    color: `${theme.palette.primary.main} !important`,
    fontWeight: "600 !important",
  },
}));

const StandardTabs = ({
  items = [],
  value,
  onChange,
  maxVisible,
  fullWidth = false,
  className = "",
  style = {},
}) => {
  const classes = useStyles();
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);

  const shouldSplit = maxVisible && items.length > maxVisible;
  const visibleItems = shouldSplit ? items.slice(0, maxVisible) : items;
  const overflowItems = shouldSplit ? items.slice(maxVisible) : [];

  const isOverflowActive = overflowItems.some((item) => item.value === value);
  const activeOverflowItem = overflowItems.find((item) => item.value === value);

  const handleTabClick = (e, item) => {
    if (item.disabled) return;
    if (onChange) {
      onChange(e, item.value);
    }
  };

  const handleMenuOpen = (e) => {
    setMenuAnchorEl(e.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleMenuItemClick = (e, itemValue) => {
    if (onChange) {
      onChange(e, itemValue);
    }
    handleMenuClose();
  };

  return (
    <div
      className={`${classes.container} ${fullWidth ? classes.fullWidth : ""} ${className}`}
      style={style}
    >
      {visibleItems.map((item) => {
        const isActive = item.value === value;
        const isDisabled = Boolean(item.disabled);
        return (
          <button
            key={item.value}
            type="button"
            disabled={isDisabled}
            className={`${classes.tabButton} ${
              isActive ? classes.tabButtonActive : ""
            } ${isDisabled ? classes.tabButtonDisabled : ""} ${fullWidth ? classes.tabButtonFull : ""}`}
            onClick={(e) => handleTabClick(e, item)}
          >
            {item.icon && <span className={classes.icon}>{item.icon}</span>}
            <span className={classes.label}>{item.label}</span>
            {item.badge !== undefined && item.badge !== null && (
              <span className={classes.badge}>{item.badge}</span>
            )}
          </button>
        );
      })}

      {shouldSplit && (
        <>
          <button
            type="button"
            className={`${classes.tabButton} ${
              isOverflowActive ? classes.tabButtonActive : ""
            }`}
            onClick={handleMenuOpen}
          >
            <span>
              {isOverflowActive && activeOverflowItem
                ? activeOverflowItem.label
                : i18n.t("common.moreOptions", "Más Opciones")}
            </span>
            <ExpandMoreIcon style={{ fontSize: 16 }} />
          </button>

          <Menu
            anchorEl={menuAnchorEl}
            keepMounted
            open={Boolean(menuAnchorEl)}
            onClose={handleMenuClose}
            classes={{ paper: classes.moreMenuPaper }}
            getContentAnchorEl={null}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "left",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "left",
            }}
          >
            {overflowItems.map((item) => {
              const isActive = item.value === value;
              const isDisabled = Boolean(item.disabled);
              return (
                <MenuItem
                  key={item.value}
                  disabled={isDisabled}
                  className={`${classes.moreMenuItem} ${
                    isActive ? classes.moreMenuItemActive : ""
                  }`}
                  onClick={(e) => !isDisabled && handleMenuItemClick(e, item.value)}
                >
                  {item.icon && <span className={classes.icon}>{item.icon}</span>}
                  <span style={{ flex: 1 }}>{item.label}</span>
                  {item.badge !== undefined && item.badge !== null && (
                    <span className={classes.badge}>{item.badge}</span>
                  )}
                </MenuItem>
              );
            })}
          </Menu>
        </>
      )}
    </div>
  );
};

export default StandardTabs;
